/**
 * glassForm.js — BisaWofa's shared "frosted glass" form system.
 *
 * This is the SINGLE SOURCE OF TRUTH for form styling and modal forms across
 * BisaWofa. The frosted-glass (Apple-glass / glassmorphism) look is the
 * project's form design principle: every modal form should be built with
 * openGlassModal() rather than hand-rolling markup + CSS.
 *
 * Two things are exported:
 *   1. injectGlassStyles()  — idempotent injection of the .glass-* CSS. Call it
 *                             if you want to style a bespoke form with the glass
 *                             primitives without using the modal builder.
 *   2. openGlassModal(spec) — builds and shows a glass modal form, returns a
 *                             Promise that resolves with the collected values
 *                             (on submit) or null (on cancel/dismiss).
 *
 * ── openGlassModal(spec) ────────────────────────────────────────────────────
 *   spec.title         {string}  heading
 *   spec.subtitle      {string}  small line under the heading (optional)
 *   spec.icon          {string}  Bootstrap-icon class, e.g. 'bi-signpost-2' (optional)
 *   spec.submitLabel   {string}  primary button text (default 'Save')
 *   spec.cancelLabel   {string}  ghost button text (default 'Cancel'); '' hides it
 *   spec.fields        {Field[]} ordered field descriptors
 *
 *   Field:
 *     name        {string}   key in the resolved value object (required)
 *     label       {string}   field label (required)
 *     type        {string}   'text' | 'select' | 'textarea' | 'readonly' | 'number' | 'tel' | 'email'
 *     placeholder {string}   placeholder text
 *     value       {string}   initial value
 *     options     {Array}    for type 'select'; entries are strings OR
 *                            { value, label } objects. A '' / empty value
 *                            renders as the placeholder option.
 *     required    {boolean}  blocks submit if empty (text-like fields)
 *     maxlength   {number}
 *     autocomplete{string}
 *     id          {string}   explicit element id (e.g. for voice targeting)
 *     voice       {boolean}  render a voice mic button (.voice-mic-btn) wired
 *                            to this field's id via data-voice-target. The
 *                            app's existing delegated voice handler picks it up.
 *
 *   Resolves with: { [name]: trimmedValue, ... }  on submit
 *                  null                            on cancel / Esc / backdrop
 *
 * To add a new form anywhere in BisaWofa, import openGlassModal and pass a
 * fields config — you get the glass styling, animation, keyboard handling,
 * focus management and mobile bottom-sheet behaviour for free.
 *
 * @module components/glassForm
 */

/**
 * Show a glass modal form.
 * @param {object} spec see module docs
 * @returns {Promise<null | Record<string,string>>}
 */
export function openGlassModal(spec = {}) {
  injectGlassStyles();

  const {
    title = '',
    subtitle = '',
    icon = '',
    submitLabel = 'Save',
    cancelLabel = 'Cancel',
    fields = [],
  } = spec;

  return new Promise((resolve) => {
    const overlay = document.createElement('div');
    overlay.className = 'glass-overlay';

    overlay.innerHTML = `
      <div class="glass-card" role="dialog" aria-modal="true" aria-labelledby="glass-title">
        <div class="glass-grabber" aria-hidden="true"></div>
        <header class="glass-header">
          ${icon ? `<div class="glass-icon" aria-hidden="true"><i class="bi ${_esc(icon)}"></i></div>` : ''}
          <div>
            <h2 id="glass-title" class="glass-title">${_esc(title)}</h2>
            ${subtitle ? `<p class="glass-subtitle">${_esc(subtitle)}</p>` : ''}
          </div>
        </header>

        <div class="glass-body">
          ${fields.map(_fieldHtml).join('')}
        </div>

        <footer class="glass-footer">
          ${cancelLabel ? `<button type="button" class="glass-btn glass-btn-ghost" data-action="cancel">${_esc(cancelLabel)}</button>` : ''}
          <button type="button" class="glass-btn glass-btn-primary" data-action="submit">${_esc(submitLabel)}</button>
        </footer>
      </div>
    `;

    document.body.appendChild(overlay);
    requestAnimationFrame(() => overlay.classList.add('glass-open'));

    const firstField = overlay.querySelector('.glass-input:not(.glass-readonly):not([disabled])');
    setTimeout(() => firstField?.focus(), 120);

    let settled = false;
    const cleanup = () => {
      overlay.classList.remove('glass-open');
      overlay.addEventListener('transitionend', () => overlay.remove(), { once: true });
      setTimeout(() => overlay.remove(), 350); // safety net
      document.removeEventListener('keydown', onKey);
    };
    const finish = (result) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(result);
    };

    const collect = () => {
      const out = {};
      for (const f of fields) {
        if (f.type === 'readonly' && f.excludeFromResult) continue;
        const el = overlay.querySelector(`[name="${CSS.escape(f.name)}"]`);
        out[f.name] = (el?.value ?? '').trim();
      }
      return out;
    };

    const validate = () => {
      let ok = true;
      for (const f of fields) {
        if (!f.required) continue;
        const el = overlay.querySelector(`[name="${CSS.escape(f.name)}"]`);
        const empty = !(el?.value ?? '').trim();
        const fieldEl = el?.closest('.glass-field');
        fieldEl?.classList.toggle('glass-field-invalid', empty);
        if (empty && ok) { el?.focus(); ok = false; }
      }
      return ok;
    };

    overlay.addEventListener('click', (e) => {
      const action = e.target.closest('[data-action]')?.dataset.action;
      if (action === 'submit') { if (validate()) finish(collect()); return; }
      if (action === 'cancel') { finish(null); return; }
      if (e.target === overlay) finish(null); // backdrop
    });

    // Clear invalid highlight as the user types.
    overlay.addEventListener('input', (e) => {
      e.target.closest('.glass-field')?.classList.remove('glass-field-invalid');
    });

    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); finish(null); }
      if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
        e.preventDefault();
        if (validate()) finish(collect());
      }
    };
    document.addEventListener('keydown', onKey);
  });
}

// ---------- field rendering ----------

function _fieldHtml(f) {
  const label = `<span class="glass-label">${_esc(f.label || '')}</span>`;
  const idAttr = f.id ? `id="${_esc(f.id)}"` : '';
  const common = `name="${_esc(f.name)}" ${idAttr} ${f.maxlength ? `maxlength="${f.maxlength}"` : ''} ` +
                 `${f.autocomplete ? `autocomplete="${_esc(f.autocomplete)}"` : ''} ` +
                 `${f.placeholder ? `placeholder="${_esc(f.placeholder)}"` : ''}`;
  const val = f.value != null ? _esc(String(f.value)) : '';

  // Optional voice mic button — reuses the app-wide .voice-mic-btn delegated
  // handler, which targets the input by id via data-voice-target.
  const mic = (f.voice && f.id)
    ? `<button type="button" class="voice-mic-btn" data-voice-target="${_esc(f.id)}" aria-label="Voice input" title="Speak">🎤</button>`
    : '';

  let control;
  switch (f.type) {
    case 'select':
      control = `
        <div class="glass-select-wrap">
          <select class="glass-input glass-select" name="${_esc(f.name)}" ${idAttr}>
            ${(f.options || []).map((o) => {
              const value = (o && typeof o === 'object') ? o.value : o;
              const text  = (o && typeof o === 'object') ? o.label : o;
              const selected = String(value) === String(f.value ?? '') ? ' selected' : '';
              const display = value === '' ? (f.placeholder || 'Select…') : _esc(text);
              return `<option value="${_esc(value)}"${selected}>${display}</option>`;
            }).join('')}
          </select>
          <i class="bi bi-chevron-down glass-select-chevron" aria-hidden="true"></i>
        </div>`;
      break;
    case 'textarea':
      control = `<textarea class="glass-input glass-textarea" ${common} rows="3">${val}</textarea>`;
      break;
    case 'readonly':
      control = `<input type="text" class="glass-input glass-readonly" name="${_esc(f.name)}" ${idAttr} value="${val}" readonly tabindex="-1" />`;
      break;
    default: { // text, number, tel, email
      const inputType = ['number', 'tel', 'email'].includes(f.type) ? f.type : 'text';
      control = `<input type="${inputType}" class="glass-input" ${common} value="${val}" />`;
    }
  }

  // Wrap with a voice mic button when requested.
  if (mic) {
    control = `<div class="voice-input-wrap">${control}${mic}</div>`;
  }

  return `<label class="glass-field">${label}${control}</label>`;
}

// ---------- shared styles (single source of truth) ----------

let _stylesInjected = false;
export function injectGlassStyles() {
  if (_stylesInjected) return;
  _stylesInjected = true;
  const css = `
    .glass-overlay {
      position: fixed; inset: 0; z-index: 2000;
      display: flex; align-items: center; justify-content: center;
      padding: 20px;
      background: rgba(20, 8, 24, 0.28);
      -webkit-backdrop-filter: blur(2px);
      backdrop-filter: blur(2px);
      opacity: 0;
      transition: opacity 0.28s ease;
    }
    .glass-overlay.glass-open { opacity: 1; }

    .glass-card {
      width: 100%; max-width: 440px;
      max-height: calc(100dvh - 40px);
      overflow-y: auto;
      border-radius: 26px;
      padding: 22px 22px 18px;
      position: relative;
      background: linear-gradient(160deg,
        rgba(255, 255, 255, 0.72) 0%,
        rgba(255, 255, 255, 0.52) 100%);
      -webkit-backdrop-filter: saturate(180%) blur(26px);
      backdrop-filter: saturate(180%) blur(26px);
      border: 1px solid rgba(255, 255, 255, 0.65);
      box-shadow:
        0 18px 50px rgba(20, 8, 24, 0.28),
        inset 0 1px 0 rgba(255, 255, 255, 0.6);
      transform: translateY(18px) scale(0.98);
      opacity: 0;
      transition: transform 0.3s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.3s ease;
    }
    .glass-open .glass-card { transform: translateY(0) scale(1); opacity: 1; }

    .glass-grabber {
      width: 38px; height: 5px; border-radius: 3px;
      background: rgba(60, 30, 60, 0.22);
      margin: 0 auto 14px;
    }

    .glass-header { display: flex; align-items: center; gap: 14px; margin-bottom: 18px; }
    .glass-icon {
      flex: 0 0 auto; width: 46px; height: 46px; border-radius: 14px;
      display: flex; align-items: center; justify-content: center;
      font-size: 22px; color: #fff;
      background: linear-gradient(150deg, #8B008B, #b0309f);
      box-shadow: 0 6px 16px rgba(139, 0, 139, 0.4);
    }
    .glass-title {
      margin: 0; font-size: 1.25rem; font-weight: 700;
      letter-spacing: -0.01em; color: #1c1020;
    }
    .glass-subtitle { margin: 2px 0 0; font-size: 0.82rem; color: rgba(40, 20, 45, 0.6); }

    .glass-body { display: flex; flex-direction: column; gap: 14px; }

    .glass-field { display: flex; flex-direction: column; gap: 6px; }
    .glass-label {
      font-size: 0.74rem; font-weight: 600; text-transform: uppercase;
      letter-spacing: 0.04em; color: rgba(40, 20, 45, 0.62); padding-left: 2px;
    }
    .glass-input {
      width: 100%; font-size: 1rem; color: #1c1020;
      padding: 12px 14px; border-radius: 14px;
      border: 1px solid rgba(255, 255, 255, 0.7);
      background: rgba(255, 255, 255, 0.55);
      -webkit-backdrop-filter: blur(8px);
      backdrop-filter: blur(8px);
      box-shadow: inset 0 1px 2px rgba(40, 20, 45, 0.06);
      outline: none;
      transition: border-color 0.15s, box-shadow 0.15s, background 0.15s;
      -webkit-appearance: none; appearance: none;
      font-family: inherit;
    }
    .glass-input::placeholder { color: rgba(40, 20, 45, 0.38); }
    .glass-input:focus {
      border-color: rgba(139, 0, 139, 0.55);
      background: rgba(255, 255, 255, 0.78);
      box-shadow: 0 0 0 4px rgba(139, 0, 139, 0.14);
    }
    .glass-textarea { resize: vertical; min-height: 72px; }
    .glass-readonly { color: rgba(40, 20, 45, 0.55); background: rgba(255, 255, 255, 0.35); cursor: default; }

    .glass-field-invalid .glass-input {
      border-color: rgba(211, 47, 47, 0.65);
      box-shadow: 0 0 0 4px rgba(211, 47, 47, 0.12);
    }

    .glass-select-wrap { position: relative; }
    .glass-select { padding-right: 38px; cursor: pointer; }
    .glass-select-chevron {
      position: absolute; right: 14px; top: 50%; transform: translateY(-50%);
      pointer-events: none; color: rgba(40, 20, 45, 0.5); font-size: 0.85rem;
    }

    .glass-footer { display: flex; gap: 10px; margin-top: 22px; }
    .glass-btn {
      flex: 1; font-size: 0.98rem; font-weight: 600;
      padding: 13px 16px; border-radius: 14px; border: none; cursor: pointer;
      transition: transform 0.1s, box-shadow 0.15s, background 0.15s, opacity 0.15s;
      -webkit-tap-highlight-color: transparent;
      font-family: inherit;
    }
    .glass-btn:active { transform: scale(0.97); }
    .glass-btn-ghost {
      flex: 0 0 auto; min-width: 92px; color: #5a2a5a;
      background: rgba(255, 255, 255, 0.45);
      border: 1px solid rgba(255, 255, 255, 0.6);
    }
    .glass-btn-ghost:hover { background: rgba(255, 255, 255, 0.65); }
    .glass-btn-primary {
      color: #fff;
      background: linear-gradient(150deg, #8B008B, #a8259c);
      box-shadow: 0 8px 20px rgba(139, 0, 139, 0.38);
    }
    .glass-btn-primary:hover { box-shadow: 0 10px 26px rgba(139, 0, 139, 0.48); }

    @media (prefers-color-scheme: dark) {
      .glass-card {
        background: linear-gradient(160deg, rgba(48, 30, 52, 0.72), rgba(34, 20, 38, 0.6));
        border-color: rgba(255, 255, 255, 0.18);
      }
      .glass-title { color: #f5eef7; }
      .glass-subtitle, .glass-label { color: rgba(245, 238, 247, 0.6); }
      .glass-input { color: #f5eef7; background: rgba(255, 255, 255, 0.1); border-color: rgba(255,255,255,0.16); }
      .glass-input::placeholder { color: rgba(245, 238, 247, 0.4); }
      .glass-grabber { background: rgba(255, 255, 255, 0.28); }
      .glass-btn-ghost { color: #f0d8f0; background: rgba(255,255,255,0.12); border-color: rgba(255,255,255,0.18); }
      .glass-readonly { color: rgba(245,238,247,0.5); background: rgba(255,255,255,0.06); }
    }

    @media (max-width: 480px) {
      .glass-overlay { align-items: flex-end; padding: 0; }
      .glass-card { border-radius: 22px 22px 0 0; max-width: 100%; align-self: flex-end; }
    }
  `;
  const styleEl = document.createElement('style');
  styleEl.id = 'glass-form-styles';
  styleEl.textContent = css;
  document.head.appendChild(styleEl);
}

// ---------- helpers ----------

function _esc(s) {
  return String(s ?? '').replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c]));
}

export default { openGlassModal, injectGlassStyles };
